import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import logoAsset from "@/assets/logo-mi-almunajah.png.asset.json";
import { toast } from "sonner";

export const Route = createFileRoute("/create-password")({
  head: () => ({ meta: [{ title: "Buat Password Baru — MI Al-Munajah Admin" }] }),
  component: CreatePasswordPage,
});

function CreatePasswordPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [valid, setValid] = useState<boolean | null>(null);

  useEffect(() => {
    const hash = window.location.hash;
    const search = window.location.search;
    const isValid =
      hash.includes("type=invite") ||
      hash.includes("type=recovery") ||
      hash.includes("access_token=") ||
      search.includes("type=invite") ||
      search.includes("type=recovery") ||
      search.includes("code=");
    setValid(isValid);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) return toast.error("Password tidak cocok.");
    if (password.length < 8) return toast.error("Password minimal 8 karakter.");
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) return toast.error(error.message);
    toast.success("Password berhasil dibuat. Silakan masuk.");
    navigate({ to: "/auth", replace: true });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-primary via-primary to-primary/80 text-primary-foreground">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-white p-1.5 flex items-center justify-center shadow-md">
            <img src={logoAsset.url} alt="MI Plus Almunajah" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="font-bold text-lg leading-tight">MI Plus Almunajah</div>
            <div className="text-xs opacity-80">Admin Panel</div>
          </div>
        </div>
        <div className="space-y-3">
          <h1 className="text-4xl font-bold leading-tight">Selamat datang<br/>di tim kami.</h1>
          <p className="text-primary-foreground/80 max-w-md">
            Buat password untuk mengaktifkan akun Anda dan mulai mengelola data sekolah.
          </p>
        </div>
        <div className="text-xs opacity-70">© {new Date().getFullYear()} MI Al-Munajah</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <Card className="w-full max-w-md p-8 rounded-3xl shadow-[var(--shadow-elevation-2)]">
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-white p-1 flex items-center justify-center shadow-sm border">
              <img src={logoAsset.url} alt="MI Plus Almunajah" className="w-full h-full object-contain" />
            </div>
            <div className="font-bold">MI Plus Almunajah</div>
          </div>

          <h2 className="text-2xl font-bold mb-1">Buat Password Baru</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Buat password untuk akun Anda agar bisa mulai masuk.
          </p>

          {valid === false ? (
            <div className="rounded-xl bg-destructive/10 p-6 text-center space-y-3">
              <div className="text-lg font-semibold text-destructive">Halaman Tidak Tersedia</div>
              <p className="text-sm text-destructive/80">
                Halaman yang Anda tuju tidak tersedia, jika Anda mengalami kendala pada akun, silahkan hubungi admin sekolah.
              </p>
              <Button asChild variant="outline" className="mt-2">
                <Link to="/auth">Kembali ke Halaman Masuk</Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password Baru</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  placeholder="Minimal 8 karakter"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">Konfirmasi Password</Label>
                <Input
                  id="confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  placeholder="Ulangi password baru"
                />
              </div>
              <Button type="submit" className="w-full rounded-xl h-11" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan & Aktifkan Akun
              </Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
